// /monitoreo — panel de administración (oculto del nav, solo ADMIN_EMAILS):
// registros de usuarios, salud de la base y seguridad (logins fallidos).
// En Vercel es serverless: cada carga es una "foto" del momento, no un monitor 24/7.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiActivity,
  FiAlertTriangle,
  FiDatabase,
  FiLock,
  FiRefreshCw,
  FiSearch,
  FiSmartphone,
  FiUsers,
} from "react-icons/fi";
import { adminService } from "../api";
import style from "../style/Monitoreo.module.css";

const fmtFecha = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const fmtFechaHora = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Últimos 30 días completos (con 0 en los días sin registros) para el gráfico
const buildDias = (porDia = []) => {
  const map = new Map(porDia.map((d) => [d._id, d.count]));
  const out = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
    const key = d.toISOString().slice(0, 10);
    out.push({ key, label: `${d.getDate()}/${d.getMonth() + 1}`, count: map.get(key) || 0 });
  }
  return out;
};

function MonitoreoPage() {
  const [overview, setOverview] = useState(null);
  const [health, setHealth] = useState(null);
  const [security, setSecurity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");
  // Aviso de actualización de la app (popup al abrir)
  const [aviso, setAviso] = useState(null);
  const [avisoEstado, setAvisoEstado] = useState(""); // "" | saving | ok | mensaje de error

  const cambiarAviso = (campo, valor) => {
    setAviso((prev) => ({ ...prev, [campo]: valor }));
    setAvisoEstado("");
  };

  const guardarAviso = async () => {
    if (!aviso) return;
    setAvisoEstado("saving");
    try {
      const res = await adminService.saveAppUpdate({
        activo: aviso.activo,
        latest: aviso.latest,
        obligatorio: aviso.obligatorio,
        title: aviso.title,
        message: aviso.message,
        changes: String(aviso.changesText || "").split("\n"),
      });
      setAviso({ ...res.data, changesText: (res.data.changes || []).join("\n") });
      setAvisoEstado("ok");
    } catch (err) {
      setAvisoEstado(err.response?.data?.message || "No se pudo guardar.");
    }
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [ov, he, se] = await Promise.all([
        adminService.overview(),
        adminService.health(),
        adminService.security(),
      ]);
      setOverview(ov.data);
      setHealth(he.data);
      setSecurity(se.data);
      // Aparte: si esto falla no tiene que tirar abajo el resto del panel
      adminService
        .appUpdate()
        .then((res) =>
          setAviso({ ...res.data, changesText: (res.data.changes || []).join("\n") })
        )
        .catch(() => {});
      setForbidden(false);
    } catch (err) {
      if (err.response?.status === 403) setForbidden(true);
      else setError("No se pudo cargar el monitoreo. Probá de nuevo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const dias = useMemo(() => buildDias(overview?.porDia), [overview]);
  const maxDia = Math.max(1, ...dias.map((d) => d.count));

  const usuariosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const lista = overview?.ultimos || [];
    if (!q) return lista;
    return lista.filter(
      (u) =>
        String(u.username || "").toLowerCase().includes(q) ||
        String(u.email || "").toLowerCase().includes(q)
    );
  }, [overview, busqueda]);

  if (forbidden) {
    return (
      <div className={style.page}>
        <div className={style.forbidden}>
          <FiLock />
          <h2>Solo administradores</h2>
          <p>Tu cuenta no está habilitada para ver esta sección.</p>
        </div>
      </div>
    );
  }

  const dbOk = health?.estado === "conectada";

  // Uso del plan de Atlas: datos + índices contra el tope (M0 gratis = 512 MB)
  const usadoMB = health?.db
    ? Number((health.db.datosMB + health.db.indicesMB).toFixed(1))
    : 0;
  const limiteMB = health?.limiteMB || 512;
  const pctUso = Number(((usadoMB / limiteMB) * 100).toFixed(1));

  return (
    <div className={style.page}>
      <header className={style.header}>
        <div>
          <h1>Monitoreo</h1>
          <p className={style.subtitle}>
            Foto al momento de cargar la página
            {health?.chequeadoEn ? ` · ${fmtFechaHora(health.chequeadoEn)}` : ""}
          </p>
        </div>
        <button className={style.refreshBtn} onClick={cargar} disabled={loading} type="button">
          <FiRefreshCw className={loading ? style.spinning : undefined} />
          {loading ? "Cargando..." : "Actualizar"}
        </button>
      </header>

      {error ? <p className={style.error}>{error}</p> : null}

      {/* ===== KPIs de usuarios ===== */}
      <section className={style.kpiGrid}>
        <div className={style.kpi}>
          <span className={style.kpiLabel}>Usuarios totales</span>
          <span className={style.kpiValue}>{overview?.total ?? "—"}</span>
        </div>
        <div className={style.kpi}>
          <span className={style.kpiLabel}>Nuevos · 24 h</span>
          <span className={`${style.kpiValue} ${style.kpiGreen}`}>{overview?.nuevos24h ?? "—"}</span>
        </div>
        <div className={style.kpi}>
          <span className={style.kpiLabel}>Nuevos · 7 días</span>
          <span className={`${style.kpiValue} ${style.kpiGreen}`}>{overview?.nuevos7d ?? "—"}</span>
        </div>
        <div className={style.kpi}>
          <span className={style.kpiLabel}>Nuevos · 30 días</span>
          <span className={`${style.kpiValue} ${style.kpiGreen}`}>{overview?.nuevos30d ?? "—"}</span>
        </div>
        <div className={style.kpi} title="Usuarios que usaron la app en los últimos 7 días (cualquier acción cuenta, no solo el login; arranca a llenarse desde este deploy)">
          <span className={style.kpiLabel}>Activos · 7 días</span>
          <span className={style.kpiValue}>{overview?.activos7d ?? "—"}</span>
        </div>
      </section>

      {/* ===== Registros por día ===== */}
      <section className={style.card}>
        <h2 className={style.cardTitle}>
          <FiUsers /> Registros por día · últimos 30
        </h2>
        <div className={style.chart}>
          {dias.map((d) => (
            <div key={d.key} className={style.chartCol} title={`${d.label}: ${d.count}`}>
              <div
                className={style.chartBar}
                style={{ height: `${Math.max(4, (d.count / maxDia) * 100)}%` }}
                data-empty={d.count === 0 ? "1" : undefined}
              />
            </div>
          ))}
        </div>
        <div className={style.chartAxis}>
          <span>{dias[0]?.label}</span>
          <span>{dias[14]?.label}</span>
          <span>{dias[29]?.label}</span>
        </div>
      </section>

      {/* ===== Aviso de actualización de la app ===== */}
      {aviso ? (
        <section className={style.card}>
          <h2 className={style.cardTitle}>
            <FiSmartphone /> Aviso de actualización de la app
          </h2>
          <p className={style.mutedSmall}>
            Al abrir la app, quien tenga una versión MENOR a la que pongas acá ve un popup para
            actualizar. Cargalo recién cuando la versión nueva esté aprobada y publicada en las
            dos tiendas.
          </p>
          <div className={style.avisoGrid}>
            <label className={style.field}>
              <span>Versión publicada en las tiendas</span>
              <input
                value={aviso.latest}
                onChange={(e) => cambiarAviso("latest", e.target.value)}
                placeholder="1.0.17"
                inputMode="decimal"
              />
            </label>
            <label className={style.field}>
              <span>Título del popup</span>
              <input
                value={aviso.title}
                onChange={(e) => cambiarAviso("title", e.target.value)}
                placeholder="¡Nueva versión disponible!"
                maxLength={120}
              />
            </label>
          </div>
          <label className={style.field}>
            <span>Mensaje</span>
            <textarea
              rows={3}
              value={aviso.message}
              onChange={(e) => cambiarAviso("message", e.target.value)}
              placeholder="Actualizá Growth para ver las últimas mejoras."
              maxLength={600}
            />
          </label>
          <label className={style.field}>
            <span>Novedades (una por línea, opcional)</span>
            <textarea
              rows={4}
              value={aviso.changesText}
              onChange={(e) => cambiarAviso("changesText", e.target.value)}
              placeholder={"Notas con páginas\nGuardado automático"}
            />
          </label>

          <div className={style.field}>
            <span>¿Se puede posponer?</span>
            <div className={style.segment}>
              <button
                type="button"
                className={!aviso.obligatorio ? style.segmentOn : ""}
                onClick={() => cambiarAviso("obligatorio", false)}
              >
                Con “Más tarde”
              </button>
              <button
                type="button"
                className={aviso.obligatorio ? style.segmentOnDanger : ""}
                onClick={() => cambiarAviso("obligatorio", true)}
              >
                Obligatorio
              </button>
            </div>
            <p className={style.mutedSmall}>
              {aviso.obligatorio
                ? "Obligatorio: el popup no se puede cerrar; hasta que actualicen no pueden usar la app. (Las versiones viejas de la app, anteriores a esta función, lo siguen pudiendo cerrar.)"
                : "Con “Más tarde”: lo ven una sola vez por versión y lo pueden cerrar."}
            </p>
          </div>

          <div className={style.avisoFooter}>
            <label className={style.check}>
              <input
                type="checkbox"
                checked={aviso.activo}
                onChange={(e) => cambiarAviso("activo", e.target.checked)}
              />
              Aviso activo
            </label>
            <span className={style.mutedSmall}>
              {avisoEstado === "ok"
                ? "Guardado ✓"
                : avisoEstado && avisoEstado !== "saving"
                ? avisoEstado
                : aviso.updatedAt
                ? `Último cambio: ${fmtFechaHora(aviso.updatedAt)}`
                : ""}
            </span>
            <button
              type="button"
              className={style.refreshBtn}
              onClick={guardarAviso}
              disabled={avisoEstado === "saving"}
            >
              {avisoEstado === "saving" ? "Guardando…" : "Guardar aviso"}
            </button>
          </div>
        </section>
      ) : null}

      <div className={style.twoCols}>
        {/* ===== Salud de la base ===== */}
        <section className={style.card}>
          <h2 className={style.cardTitle}>
            <FiDatabase /> Base de datos
          </h2>
          <div className={style.healthRow}>
            <span className={`${style.statusDot} ${dbOk ? style.dotOk : style.dotBad}`} />
            <strong>{health?.estado || "—"}</strong>
            {typeof health?.pingMs === "number" ? (
              <span className={style.muted}>· ping {health.pingMs} ms</span>
            ) : null}
          </div>
          {health?.db ? (
            <div className={style.usageWrap}>
              <div className={style.usageInfo}>
                <span>Uso del plan</span>
                <strong>
                  {usadoMB} MB de {limiteMB} MB ({pctUso}%)
                </strong>
              </div>
              <div className={style.usageTrack}>
                <div
                  className={`${style.usageFill} ${
                    pctUso >= 85 ? style.usageDanger : pctUso >= 60 ? style.usageWarn : ""
                  }`}
                  style={{ width: `${Math.min(100, Math.max(1, pctUso))}%` }}
                />
              </div>
              <p className={style.mutedSmall}>
                Atlas cuenta datos + índices. Al llegar al 100% bloquea escrituras (no se cae,
                pero no se puede guardar). Activá la alerta al 75% en Atlas → Alerts.
              </p>
            </div>
          ) : null}
          {health?.db ? (
            <ul className={style.statList}>
              <li>
                <span>Datos</span>
                <strong>{health.db.datosMB} MB</strong>
              </li>
              <li>
                <span>Almacenamiento</span>
                <strong>{health.db.almacenamientoMB} MB</strong>
              </li>
              <li>
                <span>Índices</span>
                <strong>{health.db.indicesMB} MB</strong>
              </li>
              <li>
                <span>Colecciones</span>
                <strong>{health.db.colecciones}</strong>
              </li>
              <li>
                <span>Documentos</span>
                <strong>{health.db.documentos}</strong>
              </li>
            </ul>
          ) : null}
          {health?.conteos?.length ? (
            <>
              <h3 className={style.cardSubtitle}>Documentos por sección</h3>
              <ul className={style.statList}>
                {health.conteos.map((c) => (
                  <li key={c.label}>
                    <span>{c.label}</span>
                    <strong>{c.count}</strong>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </section>

        {/* ===== Seguridad ===== */}
        <section className={style.card}>
          <h2 className={style.cardTitle}>
            <FiActivity /> Seguridad
          </h2>
          <div className={style.securityKpis}>
            <div>
              <span className={style.kpiLabel}>Logins fallidos · 24 h</span>
              <span className={`${style.kpiValue} ${security?.fallos24h ? style.kpiWarn : ""}`}>
                {security?.fallos24h ?? "—"}
              </span>
            </div>
            <div>
              <span className={style.kpiLabel}>Fallidos · 7 días</span>
              <span className={style.kpiValue}>{security?.fallos7d ?? "—"}</span>
            </div>
          </div>
          <p className={style.mutedSmall}>
            Rate limits activos: login {security?.rateLimits?.auth || "—"} · API{" "}
            {security?.rateLimits?.api || "—"}. Los intentos se guardan 30 días.
          </p>
          {security?.topIps?.length ? (
            <>
              <h3 className={style.cardSubtitle}>IPs con más fallos (7 días)</h3>
              <ul className={style.statList}>
                {security.topIps.map((ipRow) => (
                  <li key={ipRow._id || "sin-ip"}>
                    <span className={style.mono}>{ipRow._id || "sin IP"}</span>
                    <strong>{ipRow.count}</strong>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className={style.emptyNote}>
              <FiAlertTriangle /> Sin intentos fallidos registrados (el log arranca desde este
              deploy).
            </p>
          )}
          {security?.recientes?.length ? (
            <>
              <h3 className={style.cardSubtitle}>Últimos fallos</h3>
              <div className={style.tableWrap}>
                <table className={style.table}>
                  <thead>
                    <tr>
                      <th>Email intentado</th>
                      <th>IP</th>
                      <th>Motivo</th>
                      <th>Cuándo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {security.recientes.slice(0, 15).map((a) => (
                      <tr key={a._id}>
                        <td>{a.email || "—"}</td>
                        <td className={style.mono}>{a.ip || "—"}</td>
                        <td>{a.motivo === "no-user" ? "email inexistente" : "clave incorrecta"}</td>
                        <td>{fmtFechaHora(a.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </section>
      </div>

      {/* ===== Últimos registros ===== */}
      <section className={style.card}>
        <div className={style.tableHead}>
          <h2 className={style.cardTitle}>
            <FiUsers /> Últimos registros
          </h2>
          <label className={style.search}>
            <FiSearch />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por usuario o email"
            />
          </label>
        </div>
        <div className={style.tableWrap}>
          <table className={style.table}>
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>Registro</th>
                <th>Última actividad</th>
              </tr>
            </thead>
            <tbody>
              {usuariosFiltrados.map((u) => (
                <tr key={u._id}>
                  <td>{u.username || "—"}</td>
                  <td>{u.email || "—"}</td>
                  <td>{fmtFecha(u.createdAt)}</td>
                  <td>
                    {u.lastSeenAt || u.lastLoginAt
                      ? fmtFechaHora(u.lastSeenAt || u.lastLoginAt)
                      : "—"}
                  </td>
                </tr>
              ))}
              {!loading && usuariosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={4} className={style.emptyCell}>
                    Sin resultados.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default MonitoreoPage;
